import { useState, type CSSProperties } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { KeyInsight } from '@/api/deep-signal-api';
import { saveCuratedKeyInsights, deleteCuratedKeyInsights } from '@/api/deep-signal-api';
import { InsightCard } from './insight-card';

const DELTA_TYPES: KeyInsight['deltaType'][] = ['positive', 'negative', 'neutral', 'info'];

// A blank card seed for "Add insight". id is filled by the backend sanitizer if
// left empty, but a stable local id keeps React keys unique while editing.
function blankCard(seq: number): KeyInsight {
  return {
    id: `new_${seq}`,
    delta: '',
    deltaType: 'neutral',
    title: '',
    summary: '',
    body: '',
    confidence: 80,
    sources: '',
    timeAgo: '',
    aiReasoning: '',
  };
}

interface KeyInsightsPanelProps {
  /** Resolved cards for the current customer (backend override, else config). */
  insights: KeyInsight[];
  customerId: number | null;
  isAdmin: boolean;
  /** True when `insights` is the admin-curated override (enables Reset). */
  isCurated: boolean;
  onAskAll: () => void;
  onAskAI: (q: string) => void;
  /** Re-fetch the run payload (used after Reset to show the restored default). */
  onReload: () => void;
}

export function KeyInsightsPanel({
  insights,
  customerId,
  isAdmin,
  isCurated,
  onAskAll,
  onAskAI,
  onReload,
}: KeyInsightsPanelProps) {
  // After a successful save the backend's sanitized cards render immediately,
  // without a refetch (mirrors the Blueprint editor's savedOverride).
  const [savedOverride, setSavedOverride] = useState<KeyInsight[] | null>(null);
  const [draft, setDraft] = useState<KeyInsight[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [seq, setSeq] = useState(0);
  const editMode = draft !== null;

  const cards = savedOverride ?? insights;
  // A freshly saved override is curated by definition; before the payload is
  // refetched its key_insights_is_curated may still read false, so OR it in.
  const showReset = savedOverride !== null || isCurated;

  // Nothing to show and no way to add it: render nothing for client viewers.
  // Admins still get an "Add Key Insights" entry point when the list is empty.
  if (cards.length === 0 && !isAdmin && !editMode) return null;

  function startEdit() {
    setSaveError(null);
    const base = savedOverride ?? insights;
    // Deep-copy so field edits never mutate the rendered content in place.
    setDraft(base.length > 0 ? JSON.parse(JSON.stringify(base)) : [blankCard(0)]);
  }

  function updateCard(idx: number, field: keyof KeyInsight, value: string | number) {
    setDraft(prev => {
      if (!prev) return prev;
      const next = prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c));
      return next;
    });
  }

  function addCard() {
    setSeq(s => s + 1);
    setDraft(prev => [...(prev ?? []), blankCard(seq + 1)]);
  }

  function removeCard(idx: number) {
    setDraft(prev => (prev ? prev.filter((_, i) => i !== idx) : prev));
  }

  async function saveEdit() {
    if (!draft || customerId == null) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await saveCuratedKeyInsights(customerId, draft);
      setSavedOverride(res.key_insights);
      setDraft(null);
    } catch (e: any) {
      // Surface the backend's 422 detail (e.g. the em/en-dash rejection) so the
      // admin can fix the copy rather than guessing.
      setSaveError(e?.message || 'Save failed. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  async function resetToDefault() {
    if (customerId == null) return;
    setSaving(true);
    setSaveError(null);
    try {
      await deleteCuratedKeyInsights(customerId);
      setSavedOverride(null);
      setDraft(null);
      // The generated/config default now wins server-side; refetch to show it.
      onReload();
    } catch (e: any) {
      setSaveError(e?.message || 'Reset failed. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  const draftCards = draft ?? [];

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: '10px',
      overflow: 'hidden',
      backgroundColor: 'var(--background)',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--foreground)' }}>Key Insights</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Admin curation controls */}
          {isAdmin && !editMode && (
            <button
              onClick={startEdit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)',
                background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
                padding: '5px 12px', cursor: 'pointer',
              }}
            >
              <Pencil size={12} /> {cards.length > 0 ? 'Edit insights' : 'Add Key Insights'}
            </button>
          )}
          {isAdmin && editMode && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={saveEdit}
                disabled={saving}
                style={{
                  fontSize: 'var(--text-xs)', fontWeight: 600, color: '#fff',
                  backgroundColor: '#00c9c4', border: 'none', borderRadius: '8px',
                  padding: '6px 14px', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save insights'}
              </button>
              <button
                onClick={() => { setDraft(null); setSaveError(null); }}
                disabled={saving}
                style={{
                  fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)',
                  background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
                  padding: '5px 12px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              {showReset && (
                <button
                  onClick={resetToDefault}
                  disabled={saving}
                  title="Delete the curated cards and restore the generated/default insights"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--infrared-coral)',
                    background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
                    padding: '5px 12px', cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} /> Reset to default
                </button>
              )}
            </span>
          )}
          {!editMode && (
            <button
              onClick={onAskAll}
              style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-xs)', color: '#7e5fe0', fontWeight: 600, padding: '2px 0' }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <span style={{ width: 13, height: 13, borderRadius: 9999, background: 'linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))', flexShrink: 0 }} />
              Ask Upmode about all of this
            </button>
          )}
        </div>
      </div>

      {saveError && (
        <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-xs)', color: 'var(--infrared-coral)', whiteSpace: 'pre-line' }}>
          {saveError}
        </div>
      )}

      {/* Body — editor or rendered cards */}
      {editMode ? (
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {draftCards.map((c, idx) => (
            <KeyInsightEditor
              key={c.id || idx}
              card={c}
              onChange={(field, value) => updateCard(idx, field, value)}
              onRemove={() => removeCard(idx)}
              canRemove={draftCards.length > 1}
            />
          ))}
          <button
            onClick={addCard}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)',
              background: 'none', border: '1px dashed var(--border)', borderRadius: '8px',
              padding: '8px 12px', cursor: 'pointer',
            }}
          >
            <Plus size={13} /> Add insight
          </button>
        </div>
      ) : (
        <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {cards.map((insight) => (
            <InsightCard key={insight.id} insight={insight} onAskAI={onAskAI} />
          ))}
        </div>
      )}
    </div>
  );
}

// One editable Key Insight card. Fields map 1:1 to the KeyInsight interface the
// backend validates against.
function KeyInsightEditor({
  card,
  onChange,
  onRemove,
  canRemove,
}: {
  card: KeyInsight;
  onChange: (field: keyof KeyInsight, value: string | number) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const inputStyle: CSSProperties = {
    width: '100%',
    fontSize: 'var(--text-sm)',
    color: 'var(--foreground)',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    padding: '6px 10px',
    backgroundColor: 'var(--card)',
    fontFamily: 'inherit',
  };
  const labelStyle: CSSProperties = {
    fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)',
    marginBottom: '4px', display: 'block',
  };

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: '10px',
      padding: '14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      backgroundColor: 'var(--background)',
    }}>
      {/* Row 1: delta / deltaType / confidence / remove */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: '120px' }}>
          <label style={labelStyle}>Delta</label>
          <input value={card.delta} onChange={e => onChange('delta', e.target.value)} placeholder="+12%" style={inputStyle} />
        </div>
        <div style={{ width: '140px' }}>
          <label style={labelStyle}>Delta type</label>
          <select value={card.deltaType} onChange={e => onChange('deltaType', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            {DELTA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ width: '130px' }}>
          <label style={labelStyle}>Confidence (%)</label>
          <input
            type="number"
            min={0}
            max={100}
            value={card.confidence}
            onChange={e => onChange('confidence', e.target.value === '' ? 0 : Number(e.target.value))}
            style={inputStyle}
          />
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={onRemove}
            disabled={!canRemove}
            title={canRemove ? 'Remove this insight' : 'At least one insight is required'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              fontSize: 'var(--text-xs)', fontWeight: 600,
              color: canRemove ? 'var(--infrared-coral)' : 'var(--muted-foreground)',
              background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
              padding: '5px 10px', cursor: canRemove ? 'pointer' : 'not-allowed', opacity: canRemove ? 1 : 0.5,
            }}
          >
            <Trash2 size={12} /> Remove
          </button>
        </div>
      </div>

      <div>
        <label style={labelStyle}>Title</label>
        <input value={card.title} onChange={e => onChange('title', e.target.value)} style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>Summary</label>
        <textarea value={card.summary} onChange={e => onChange('summary', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>
      <div>
        <label style={labelStyle}>Body</label>
        <textarea value={card.body} onChange={e => onChange('body', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>
      <div>
        <label style={labelStyle}>AI reasoning</label>
        <textarea value={card.aiReasoning} onChange={e => onChange('aiReasoning', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      {/* Row: sources / timeAgo */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '160px' }}>
          <label style={labelStyle}>Sources</label>
          <input value={card.sources} onChange={e => onChange('sources', e.target.value)} placeholder="4 models, 120 queries" style={inputStyle} />
        </div>
        <div style={{ flex: 1, minWidth: '160px' }}>
          <label style={labelStyle}>Time ago</label>
          <input value={card.timeAgo} onChange={e => onChange('timeAgo', e.target.value)} placeholder="Updated today" style={inputStyle} />
        </div>
      </div>
    </div>
  );
}
